<template>
  <div
    class="value-card"
    :class="{ 'has-action': actionText }"
  >
    <div class="card-inner">
      <div class="value-icon">
        <van-icon :name="icon" />
      </div>

      <h3 class="value-title">
        {{ title }}
      </h3>

      <p class="value-desc">
        {{ description }}
      </p>

      <div
        v-if="actionText"
        class="card-action"
      >
        <van-button
          round
          class="action-button"
          @click="handleActionClick"
        >
          {{ actionText }}
          <van-icon
            name="arrow"
            class="action-arrow"
          />
        </van-button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { defineProps } from 'vue'

defineProps<{
  icon: string
  title: string
  description: string
  actionText?: string
}>()

const emit = defineEmits<{(e: 'action-click'): void }>()

const handleActionClick = () => {
  emit('action-click')
}
</script>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

.value-card {
  background: $white;
  border-radius: $border-radius-lg;
  box-shadow: $card-shadow;
  transition: all $transition-normal;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid rgba($primary-blue, 0.05);

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 12px 30px rgba(10, 30, 60, 0.15);

    .value-icon {
      transform: scale(1.1);
      background: linear-gradient(135deg, $accent-gold 0%, darken($accent-gold, 10%) 100%);
    }
  }
}

.card-inner {
  padding: $spacing-xl;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.value-icon {
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background: linear-gradient(135deg, $accent-gold 0%, lighten($accent-gold, 15%) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto $spacing-lg;
  transition: all $transition-normal;

  :deep(.van-icon) {
    font-size: 32px;
    color: $white;
  }
}

.value-title {
  font-size: $font-size-lg;
  font-weight: 700;
  text-align: center;
  margin-bottom: $spacing-md;
  color: $primary-blue;
  line-height: 1.3;
}

.value-desc {
  color: $text-light;
  text-align: center;
  line-height: 1.6;
  margin-bottom: $spacing-xl;
  flex-grow: 1;
}

.card-action {
  text-align: center;
  margin-top: auto;
}

.action-button {
  margin-top: 15px;
  background: linear-gradient(135deg, $primary-blue 0%, lighten($primary-blue, 10%) 100%);
  border: none;
  color: $white;
  font-weight: 500;
  padding: 5px 24px;
  box-shadow: 0 4px 10px rgba($primary-blue, 0.2);
  //font-size: xx-small;

  &:hover {
    background: linear-gradient(135deg, lighten($primary-blue, 5%) 0%, lighten($primary-blue, 15%) 100%);
    box-shadow: 0 6px 15px rgba($primary-blue, 0.3);
  }
}

.action-arrow {
  margin-left: $spacing-xs;
  transition: transform $transition-normal;

  .action-button:hover & {
    transform: translateX(3px);
  }
}

// 1080×1920 设备适配 (竖屏大屏设备)
@media (min-width: 1080px) and (min-height: 1920px) {
  .card-inner {
    padding: $spacing-xl * 1.5;
    min-width: 330px;
  }

  .value-icon {
    width: 90px;
    height: 90px;
    margin-bottom: $spacing-xl;

    :deep(.van-icon) {
      font-size: 40px;
    }
  }

  .value-title {
    font-size: $font-size-xl;
    margin-bottom: $spacing-lg;
  }

  .value-desc {
    font-size: $font-size-lg;
    margin-bottom: $spacing-xl * 1.5;
  }

  .action-button {
    margin-top: 20px;
    padding: 14px 32px;
    font-size: $font-size-md;
  }
}

// 平板设备 (768px - 1199px)
@media (max-width: 1199px) and (min-width: 768px) {
  .card-inner {
    padding: $spacing-lg;
    min-width: 330px;
  }

  .value-icon {
    width: 60px;
    height: 60px;
    margin-bottom: $spacing-md;

    :deep(.van-icon) {
      font-size: 28px;
    }
  }

  .value-title {
    font-size: $font-size-md;
    margin-bottom: $spacing-sm;
  }

  .value-desc {
    font-size: $font-size-sm;
    margin-bottom: $spacing-lg;
  }
}

// 移动端适配 (767px 及以下)
@media (max-width: 767px) {
  .card-inner {
    padding: $spacing-lg;
  }

  .value-icon {
    width: 60px;
    height: 60px;

    :deep(.van-icon) {
      font-size: 26px;
    }
  }

  .value-title {
    font-size: $font-size-md;
  }

  .value-desc {
    font-size: $font-size-sm;
  }

  .action-button {
    padding: 8px 20px;
    font-size: $font-size-sm;
  }
}

// 超小屏幕设备 (480px 及以下)
@media (max-width: 480px) {
  .card-inner {
    padding: $spacing-md;
  }

  .value-icon {
    width: 50px;
    height: 50px;
    margin-bottom: $spacing-sm;

    :deep(.van-icon) {
      font-size: 22px;
    }
  }

  .value-title {
    font-size: $font-size-sm;
    margin-bottom: $spacing-xs;
  }

  .value-desc {
    font-size: $font-size-xs;
    margin-bottom: $spacing-md;
  }

  .action-button {
    padding: 6px 16px;
    font-size: $font-size-xs;
  }
}

// 横屏模式适配
@media (max-height: 500px) and (orientation: landscape) {
  .value-card {
    display: flex;
    flex-direction: row;
    align-items: center;
  }

  .card-inner {
    display: flex;
    flex-direction: row;
    align-items: center;
    width: 100%;
    padding: $spacing-md;
  }

  .value-icon {
    margin: 0 $spacing-md 0 0;
    flex-shrink: 0;
  }

  .value-content {
    flex-grow: 1;
    margin-right: $spacing-md;
  }

  .value-title,
  .value-desc {
    text-align: left;
  }

  .value-title {
    margin-bottom: $spacing-xs;
  }

  .value-desc {
    margin-bottom: 0;
  }

  .card-action {
    margin-top: 0;
    flex-shrink: 0;
  }
}

// 暗色模式支持
@media (prefers-color-scheme: dark) {
  .value-card {
    background: #1e293b;
    border-color: rgba($white, 0.1);
  }

  .value-title {
    color: $white;
  }

  .value-desc {
    color: rgba($white, 0.7);
  }

  .action-button {
    background: linear-gradient(135deg, $accent-gold 0%, darken($accent-gold, 10%) 100%);

    &:hover {
      background: linear-gradient(135deg, lighten($accent-gold, 5%) 0%, $accent-gold 100%);
    }
  }
}

// 减少动画（适用于偏好减少动画的用户）
@media (prefers-reduced-motion: reduce) {
  .value-card,
  .value-icon,
  .action-arrow {
    transition: none;
  }
}
</style>
